import { createGlobalHeaders, resolveBaseUrl } from '../shared/services/create-service'
import { logger } from './logger'

/**
 * Reconnecting GET-SSE client for the playground event stream (SER-6086).
 *
 * Deliberately separate from `createSSEStream` (POST-only, drops `id:` lines,
 * no reconnect): this stream is long-lived and resumes via `Last-Event-ID`,
 * which the runner maps back onto its redis Streams positions.
 */

export interface PlaygroundEventBody {
  type: string
  content: {
    eventName: string
    eventData: Record<string, unknown>
  }
}

export interface PlaygroundStreamHandlers {
  onEvent: (body: PlaygroundEventBody) => void
  // terminal: the stream is gone for good (401/403) and will not reconnect —
  // transient drops reconnect on their own and are not worth surfacing.
  onError?: (error: unknown, info?: { terminal: boolean }) => void
}

export interface PlaygroundStream {
  close: () => void
}

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 15000

const open = (
  aomUuid: string,
  phloUuid: string,
  handlers: PlaygroundStreamHandlers,
): PlaygroundStream => {
  let closed = false
  let controller: AbortController | null = null
  let lastEventId = ''
  let reconnectDelay = RECONNECT_BASE_MS

  const consumeFrame = (frame: string) => {
    let dataLine = ''
    for (const line of frame.split('\n')) {
      if (line.startsWith('id:')) lastEventId = line.slice(3).trim()
      if (line.startsWith('data:')) dataLine = line.slice(5).trim()
      // lines starting with ':' are keepalives — nothing to do
    }
    if (!dataLine) return
    reconnectDelay = RECONNECT_BASE_MS
    try {
      handlers.onEvent(JSON.parse(dataLine) as PlaygroundEventBody)
    } catch (error) {
      logger.error('playground stream parse error', error, { dataLine })
      handlers.onError?.(error)
    }
  }

  const connect = async () => {
    while (!closed) {
      controller = new AbortController()
      try {
        const baseUrl = resolveBaseUrl(true).replace(/\/$/, '')
        const res = await fetch(`${baseUrl}/v1/playground-stream/events/${aomUuid}/${phloUuid}`, {
          method: 'GET',
          headers: {
            ...createGlobalHeaders(),
            Accept: 'text/event-stream',
            ...(lastEventId && { 'Last-Event-ID': lastEventId }),
          },
          signal: controller.signal,
        })
        if (res.status === 401 || res.status === 403) {
          // The session is gone or the org lost access; redialling forever would
          // just hammer hodor in the background. Surface it and stop.
          closed = true
          handlers.onError?.(new Error(`playground stream unauthorized (${res.status})`), {
            terminal: true,
          })
          return
        }
        if (!res.ok || !res.body) {
          throw new Error(`playground stream HTTP ${res.status}`)
        }
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        try {
          while (!closed) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const frames = buffer.split('\n\n')
            buffer = frames.pop() || ''
            frames.forEach(consumeFrame)
          }
        } finally {
          reader.releaseLock()
        }
      } catch (error) {
        if (closed || (error as Error).name === 'AbortError') return
        logger.debug('playground stream dropped, reconnecting', error)
        handlers.onError?.(error)
      }
      if (closed) return
      // dropped or server-closed: back off, then resume from lastEventId
      await new Promise((resolve) => setTimeout(resolve, reconnectDelay))
      reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS)
    }
  }

  connect()

  return {
    close: () => {
      closed = true
      controller?.abort()
    },
  }
}

// Exported as an object, not a bare function, so tests can swap `open` by
// assignment and restore it. Replacing this module with `mock.module` instead
// only works when the test file happens to load before anything that imports
// use-run-events, which made the suite order-dependent (CLAUDE.md failure
// mode 1).
export const playgroundStream = { open }
